import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { analyticsService } from '../services/analyticsService';
import './LegalPage.css';

const PrivacyPolicy = () => {
  useEffect(() => {
    analyticsService.trackPageView('privacy-policy');
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="legal-page">
      <div className="legal-header">
        <Link to="/" className="back-link">← Back to Home</Link>
        <h1>Privacy Policy</h1>
        <p className="last-updated">Last Updated: November 22, 2025</p>
      </div>

      <div className="legal-content">
        <section>
          <h2>1. Introduction</h2>
          <p>
            Learn for Fun ("we", "us", or "our") is committed to protecting your privacy. This Privacy Policy
            explains how we collect, use, disclose, and safeguard your information when you use our educational
            platform at learnforfun.online ("Platform" or "Service").
          </p>
          <p>
            Please read this Privacy Policy carefully. By using the Platform, you agree to the collection and
            use of information in accordance with this policy.
          </p>
        </section>

        <section>
          <h2>2. Information We Collect</h2>
          
          <h3>2.1 Personal Information</h3>
          <p>
            We collect personal information that you voluntarily provide when you:
          </p>
          <ul>
            <li>Create an account (name, email address, password)</li>
            <li>Register for sessions or courses</li>
            <li>Submit homework or assignments</li>
            <li>Communicate with us or other users</li>
            <li>Participate in virtual classrooms</li>
          </ul>

          <h3>2.2 Student Information</h3>
          <p>
            For student accounts, we may collect:
          </p>
          <ul>
            <li>Full name and preferred display name</li>
            <li>Email address (parent/guardian email for users under 13)</li>
            <li>Age or date of birth (for age-appropriate content)</li>
            <li>Profile avatar or photo (optional)</li>
            <li>Educational progress and performance data</li>
          </ul>

          <h3>2.3 Usage Information</h3>
          <p>
            We automatically collect certain information when you use our Platform:
          </p>
          <ul>
            <li>Browser type and version</li>
            <li>Device information (type, operating system)</li>
            <li>IP address and location data</li>
            <li>Pages visited and time spent on pages</li>
            <li>Session duration and frequency of use</li>
            <li>Interactions with features and content</li>
          </ul>

          <h3>2.4 Educational Data</h3>
          <p>
            We collect information related to your learning activities:
          </p>
          <ul>
            <li>Session registrations and attendance</li>
            <li>Homework submissions and grades</li>
            <li>Code and projects created on the Platform</li>
            <li>Progress through courses and topics</li>
            <li>Quiz and assessment results</li>
            <li>Feedback and communications with coaches</li>
          </ul>

          <h3>2.5 Cookies and Tracking Technologies</h3>
          <p>
            We use cookies and similar technologies to:
          </p>
          <ul>
            <li>Remember your preferences and settings</li>
            <li>Authenticate your identity</li>
            <li>Analyze how you use the Platform</li>
            <li>Improve user experience</li>
          </ul>
        </section>

        <section>
          <h2>3. How We Use Your Information</h2>
          <p>
            We use the information we collect to:
          </p>
          <ul>
            <li><strong>Provide Services:</strong> Create and manage your account, deliver sessions, track progress</li>
            <li><strong>Improve Platform:</strong> Analyze usage patterns, fix bugs, develop new features</li>
            <li><strong>Communication:</strong> Send session updates, homework reminders, platform announcements</li>
            <li><strong>Safety:</strong> Monitor for inappropriate content, prevent fraud and abuse</li>
            <li><strong>Legal Compliance:</strong> Comply with legal obligations and protect our rights</li>
            <li><strong>Analytics:</strong> Understand platform usage and optimize educational outcomes</li>
          </ul>
        </section>

        <section>
          <h2>4. Firebase and Third-Party Services</h2>
          <p>
            Our Platform uses Firebase and other third-party services:
          </p>

          <h3>4.1 Firebase Services</h3>
          <p>
            We use Firebase (by Google) for:
          </p>
          <ul>
            <li><strong>Firebase Authentication:</strong> Secure user authentication and account management</li>
            <li><strong>Cloud Firestore:</strong> Database storage for user data, sessions, and content</li>
            <li><strong>Cloud Storage:</strong> File uploads and homework submissions</li>
            <li><strong>Firebase Hosting:</strong> Platform hosting and delivery</li>
          </ul>
          <p>
            Firebase's use of data is governed by Google's Privacy Policy. Learn more at:
            <a href="https://firebase.google.com/support/privacy" target="_blank" rel="noopener noreferrer">
              https://firebase.google.com/support/privacy
            </a>
          </p>

          <h3>4.2 Other Third-Party Services</h3>
          <p>
            We may use additional services for:
          </p>
          <ul>
            <li>Payment processing (secure payment gateways)</li>
            <li>Email communications</li>
            <li>Analytics and performance monitoring</li>
            <li>Video conferencing for virtual classrooms</li>
          </ul>
        </section>

        <section>
          <h2>5. Information Sharing and Disclosure</h2>
          
          <h3>5.1 We Do Not Sell Your Information</h3>
          <p>
            We do not sell, rent, or trade your personal information to third parties for their marketing purposes.
          </p>

          <h3>5.2 When We Share Information</h3>
          <p>
            We may share your information in the following circumstances:
          </p>
          <ul>
            <li><strong>With Coaches:</strong> Your coach can see your profile, progress, and assignments for their sessions</li>
            <li><strong>With Classmates:</strong> Your name and avatar may be visible to other students in your sessions</li>
            <li><strong>Service Providers:</strong> Third-party vendors who help us operate the Platform (e.g., Firebase, payment processors)</li>
            <li><strong>Legal Requirements:</strong> When required by law, court order, or to protect our rights</li>
            <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
            <li><strong>With Consent:</strong> When you give us permission to share your information</li>
          </ul>
        </section>

        <section>
          <h2>6. Children's Privacy (COPPA Compliance)</h2>
          <p>
            We take children's privacy seriously and comply with the Children's Online Privacy Protection Act (COPPA).
          </p>

          <h3>6.1 Age Requirements</h3>
          <ul>
            <li>Students under 13 require verifiable parental consent</li>
            <li>We collect only information necessary for educational purposes</li>
            <li>Parents can review, update, or delete their child's information</li>
          </ul>

          <h3>6.2 Parental Rights</h3>
          <p>
            Parents and guardians have the right to:
          </p>
          <ul>
            <li>Review their child's personal information</li>
            <li>Refuse further collection or use of their child's information</li>
            <li>Delete their child's account and information</li>
            <li>Control who can communicate with their child on the Platform</li>
          </ul>

          <h3>6.3 Limited Data Collection for Children</h3>
          <p>
            For users under 13, we collect only:
          </p>
          <ul>
            <li>Essential account information (name, email)</li>
            <li>Educational data necessary for learning</li>
            <li>Information to ensure platform safety</li>
          </ul>
        </section>

        <section>
          <h2>7. Data Security</h2>
          <p>
            We implement appropriate security measures to protect your information:
          </p>
          <ul>
            <li><strong>Encryption:</strong> Data is encrypted in transit (SSL/TLS) and at rest</li>
            <li><strong>Authentication:</strong> Secure password requirements and session management</li>
            <li><strong>Access Controls:</strong> Limited access to personal data by authorized personnel only</li>
            <li><strong>Regular Audits:</strong> Security reviews and updates to protect against threats</li>
            <li><strong>Firebase Security:</strong> Built-in security rules and authentication from Firebase</li>
          </ul>
          <p>
            However, no method of transmission over the Internet is 100% secure. We cannot guarantee absolute security.
          </p>
        </section>

        <section>
          <h2>8. Data Retention</h2>
          <p>
            We retain your information for as long as necessary to:
          </p>
          <ul>
            <li>Provide our services and maintain your account</li>
            <li>Comply with legal obligations</li>
            <li>Resolve disputes and enforce agreements</li>
            <li>Maintain educational records</li>
          </ul>
          <p>
            When you delete your account, we will delete or anonymize your personal information within a reasonable
            timeframe, except where we must retain it for legal or legitimate business purposes.
          </p>
        </section>

        <section>
          <h2>9. Your Rights and Choices</h2>
          
          <h3>9.1 Access and Update</h3>
          <p>
            You can access and update your account information through your profile settings.
          </p>

          <h3>9.2 Delete Account</h3>
          <p>
            You can request deletion of your account by contacting us. We will delete your personal information
            subject to legal and operational requirements.
          </p>

          <h3>9.3 Opt-Out of Communications</h3>
          <p>
            You can opt out of promotional emails by clicking "unsubscribe" in any email or adjusting your
            notification settings. Note that you cannot opt out of essential service communications.
          </p>

          <h3>9.4 Cookie Preferences</h3>
          <p>
            You can control cookies through your browser settings, though some Platform features may not function
            properly if you disable cookies.
          </p>

          <h3>9.5 Data Portability</h3>
          <p>
            You can request a copy of your personal data in a portable format.
          </p>
        </section>

        <section>
          <h2>10. International Users</h2>
          <p>
            Our Platform is hosted in the United States. If you access the Platform from outside the United States,
            your information will be transferred to, stored, and processed in the United States. By using the Platform,
            you consent to this transfer and processing.
          </p>
        </section>

        <section>
          <h2>11. California Privacy Rights (CCPA)</h2>
          <p>
            California residents have additional rights under the California Consumer Privacy Act (CCPA):
          </p>
          <ul>
            <li>Right to know what personal information is collected</li>
            <li>Right to know if personal information is sold or disclosed</li>
            <li>Right to opt-out of the sale of personal information (we do not sell your information)</li>
            <li>Right to request deletion of personal information</li>
            <li>Right to non-discrimination for exercising privacy rights</li>
          </ul>
          <p>
            To exercise these rights, contact us at info@learnforfun.online
          </p>
        </section>

        <section>
          <h2>12. Changes to This Privacy Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. We will notify you of material changes by:
          </p>
          <ul>
            <li>Posting the updated policy on the Platform</li>
            <li>Updating the "Last Updated" date</li>
            <li>Sending an email notification for significant changes</li>
          </ul>
          <p>
            Your continued use of the Platform after changes constitutes acceptance of the updated Privacy Policy.
          </p>
        </section>

        <section>
          <h2>13. Contact Us</h2>
          <p>
            If you have questions, concerns, or requests regarding this Privacy Policy or your personal information,
            please contact us:
          </p>
          <div className="contact-info">
            <p><strong>Learn for Fun - Privacy Team</strong></p>
            <p>Email: info@learnforfun.online</p>
            <p>Website: learnforfun.online</p>
          </div>
          <p>
            For parents with questions about their child's information, please include "Parent Inquiry" in your
            subject line.
          </p>
        </section>

        <div className="acceptance-note">
          <p>
            <strong>By using Learn for Fun, you acknowledge that you have read and understood this Privacy Policy
            and agree to its terms.</strong>
          </p>
        </div>
      </div>

      <div className="legal-footer">
        <Link to="/">Return to Home</Link>
        <span>•</span>
        <Link to="/terms-of-service">Terms of Service</Link>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
